import type { EngineBackdropConfig } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop/config/types';

export const ENGINE_BACKDROP_CONFIG: EngineBackdropConfig = {
  ground: {
    // THE RAKE, which used to be a real rotation on a real plane
    // (x: 129, z: -40 on a 60-unit quad) and is now a per-pixel
    // projection. See groundProjection for why it moved.
    //
    // Tune these in /lcv/dev/engine-backdrop-lab and paste the result
    // back here -- the lab's panels are laid out to match this block.
    projection: {
      // FLAT-ON: the lattice is square to the screen and fills it, with
      // no recession at all. 0 makes the whole projection an identity.
      //
      // This is the starting point rather than the destination -- dial it
      // up in the lab to rake the floor back toward `horizon`, and paste
      // the value you land on. It defaults to aligned because a raked grid
      // is a composition choice, whereas "fills the frame" is what the
      // backdrop has to do in every case.
      pitch: 0.8,
      // Square to the frame. The old pose's -40 about Z ran the lattice
      // diagonally; that reads well once the floor is raked, but flat-on
      // it just looks tilted.
      rollDeg: -36,
      // Just above the middle of the screen. Everything below is floor.
      horizon: 2,
      horizonFeather: 0.4,
      panX: 0,
      panY: 0,
      scale: 0.3,
      // Two triangles. Nothing displaces vertices any more.
      segments: 1,
    },

    // NOTE ON UNITS. The values below that are measured in SPACE rather
    // than in ratios have all been rescaled, because the space changed.
    // The lattice, the wave and the sweep used to be measured in world
    // units across a 60-unit quad (so coordinates ran to about +/-30);
    // they are now in floor coordinates, where the near edge of the screen
    // is about +/-1. Anything spatial is therefore roughly fifteen times
    // smaller than the number it replaced -- see each one below.
    grid: {
      // WAS 400, over a uv that spanned the whole 60-unit quad. The floor
      // coordinate now spans about 1.8 across the screen's near edge, so
      // the same number would put ~720 cells across the frame and alias
      // into mush. This is cells-per-floor-unit: ~70 across a wide screen.
      count: 95,
      compression: 0.15,
      dotRadius: 0.1,
      dotSoftness: 0.15,
      markEvery: 9,
      markArmLength: 0.28,
      markArmThickness: 0.06,
      markBoost: 3.8,
      // THE FADE IS A CORNER VIGNETTE, not a hole.
      //
      // Both are fractions of the way out to the CORNER of the screen, so
      // 1 reaches the corners exactly and anything beyond that never
      // finishes fading. Holding full strength to 0.85 and running out
      // past the corner is what lets the dots fill the frame while still
      // softening where they meet the edge.
      //
      // These were 0 and 0.7 against an un-normalised radius, which is
      // what made the grid read as receding into a small central ellipse.
      fadeStart: 0.9,
      fadeEnd: 4.4,
      brightness: 1.2,
      dotCeiling: 0.45,
      opacity: 0.5,
      colorLow: '#641008',
      colorMid: '#c81e00',
      colorHigh: '#ff5a2e',
      colorLowTo: '#641008',
      colorMidTo: '#c81e00',
      colorHighTo: '#ff5a2e',
    },

    idle: {
      height: 0.025,
      frequency: 0.01,
      speed: 0.9,
      glow: 4,
      crestFade: 0.15,
      troughFade: 1.2,
      warp: 6,
      // WAS 0.27, for the same reason as `frequency` above.
      noiseScale: 1,
      noiseSpeed: 0.2,
      noiseMix: 0.78,
      fadeInSeconds: 1.5,
    },

    // The entrance. 0.78s is the intro's own ring-bump duration
    // (TIMELINE_CONFIG.bump.durationSeconds * 1.2), which is what the
    // sweep used to be timed against -- keeping it means the sweep
    // crosses the floor at the pace it was tuned to.
    reveal: {
      sweepSeconds: 0.18,
      // HOW FAR THE FRONT TRAVELS, in floor-coordinate units.
      //
      // Flat-on the furthest drawn pixel is the screen corner, at about
      // 2.2 with the default scale, so this just clears it and the sweep
      // uses its whole `sweepSeconds`. Raking the floor pushes the far
      // ground to much larger coordinates, so raise this alongside
      // `pitch` or the entrance will appear to stop halfway up the frame.
      reach: 1,
      // WAS 2.75 in world units on the 60-unit quad.
      softness: 0.35,
      turnOnStrength: 10,
      turnOnDecay: 0.1,
    },

    // NO BANG. The flash existed to sell an engine hitting the floor.
    flash: { strength: 2.25, decay: 0.1 },
  },
};
